import React, { useState, useRef, useEffect, AriaAttributes } from 'react';

import { DateRange } from './DatePicker';

import { useDisclosure } from 'system';

const formatShortDate = (date: Date, format = 'en-US') => {
  return date.toLocaleDateString(format, { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const isInvalidDate = (date: Date) => {
  return Number.isNaN(date.getTime());
};

type DatePickerHookProps = {
  onChange?: (date: Date | undefined) => void;
  selected?: Date;
  rangeSelected?: DateRange;
  onRangeChange?: (date: DateRange | undefined) => void;
  isRange?: boolean;
  dateFormat?: string;
};

type DatePickerHook = {
  isOpen: boolean;
  isInputFocused: boolean;
  setIsInputFocused: React.Dispatch<React.SetStateAction<boolean>>;
  setInputRef: React.Dispatch<React.SetStateAction<HTMLInputElement | undefined>>;
  setInputRangeStartRef: React.Dispatch<React.SetStateAction<HTMLInputElement | undefined>>;
  setInputRangeEndRef: React.Dispatch<React.SetStateAction<HTMLInputElement | undefined>>;
  boxRef: React.RefObject<HTMLDivElement>;
  buttonRef: React.RefObject<HTMLButtonElement>;
  footerRef: React.RefObject<HTMLDivElement>;
  buttonProps: AriaAttributes;
  disclosureProps: AriaAttributes;
  toogleFocusTodayDate: () => void;
  handleClickCalendarButton: () => void;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onInputRangeStartChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onInputRangeEndChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onCalendarChange: (
    date: Date | [Date | null, Date | null] | null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: React.SyntheticEvent<any, Event> | undefined
  ) => void;
  getInputValue: () => string | undefined;
  getInputRangeStartValue: () => string | undefined;
  getInputRangeEndValue: () => string | undefined;
  onClickOutside: (event: React.MouseEvent<HTMLDivElement>) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

const useDatePicker = (props: DatePickerHookProps): DatePickerHook => {
  const { onChange, selected, rangeSelected, onRangeChange, isRange, dateFormat } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const { getDisclosureProps, getButtonProps } = useDisclosure({ isOpen });
  const [inputRef, setInputRef] = useState<HTMLInputElement>();
  const [inputRangeStartRef, setInputRangeStartRef] = useState<HTMLInputElement>();
  const [inputRangeEndRef, setInputRangeEndRef] = useState<HTMLInputElement>();
  const boxRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [focusTodayDateState, setFocusTodayDateState] = useState(false);

  const toogleFocusTodayDate = () => setFocusTodayDateState(previousState => !previousState);
  const buttonProps = getButtonProps();
  const disclosureProps = getDisclosureProps();

  useEffect(() => {
    if (isRange && rangeSelected) {
      if (!inputRangeStartRef || !inputRangeEndRef) {
        return;
      }

      const { start, end } = rangeSelected;
      inputRangeStartRef.value = start && !isInvalidDate(start) ? formatShortDate(start, dateFormat) : '';
      inputRangeEndRef.value = end && !isInvalidDate(end) ? formatShortDate(end, dateFormat) : '';
      return;
    }

    if (!inputRef) {
      return;
    }

    if (selected && !isInvalidDate(selected)) {
      inputRef.value = formatShortDate(selected, dateFormat);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef, inputRangeStartRef, inputRangeEndRef]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // swap the header with the month sections to reorder tab navigation
    const monthContainer = boxRef.current?.querySelector('.react-datepicker__month-container');
    monthContainer?.insertBefore(monthContainer.children[1], monthContainer.children[0]);

    const selectedDate = boxRef.current?.querySelector('[aria-selected=true]') as HTMLDivElement | null;
    if (selectedDate) {
      selectedDate.focus();
      return;
    }

    const currentDate = boxRef.current?.querySelector('[aria-current=date]') as HTMLDivElement | undefined;
    currentDate?.focus();
  }, [isOpen, boxRef]);

  useEffect(() => {
    const todayDate = boxRef.current?.querySelector('[class*=react-datepicker__day--today]') as HTMLDivElement | null;
    todayDate?.focus();
  }, [focusTodayDateState]);

  const handleClickCalendarButton = () => {
    setIsOpen(!isOpen);
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const dateString = event.target.value;

    if (!dateString) {
      if (!isRange) {
        onChange?.(undefined);
        return;
      }
      onRangeChange?.(undefined);
      return;
    }

    const isDateFilledCompletly = !dateString.includes(' ');
    if (!isDateFilledCompletly) {
      onChange?.(undefined);
      return;
    }

    const date = new Date(dateString);
    onChange?.(date);
  };

  const onInputRangeStartChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const dateString = event.target.value;

    if (!dateString) {
      onRangeChange?.({ start: undefined, end: rangeSelected?.end });
      return;
    }

    const isDateFilledCompletly = !dateString.includes(' ');
    if (!isDateFilledCompletly) {
      onRangeChange?.({ start: undefined, end: rangeSelected?.end });
      return;
    }

    const date = new Date(dateString);
    onRangeChange?.({ start: date, end: rangeSelected?.end });
  };

  const onInputRangeEndChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const dateString = event.target.value;

    if (!dateString) {
      onRangeChange?.({ end: undefined, start: rangeSelected?.start });
      return;
    }

    const isDateFilledCompletly = !dateString.includes(' ');
    if (!isDateFilledCompletly) {
      onRangeChange?.({ end: undefined, start: rangeSelected?.start });
      return;
    }

    const date = new Date(dateString);
    onRangeChange?.({ end: date, start: rangeSelected?.start });
  };

  const onCalendarChange = (value: Date | [Date | null, Date | null] | null) => {
    if (isRange) {
      const dateRange = { start: value?.[0], end: value?.[1] };
      onRangeChange?.(dateRange);

      if (inputRangeStartRef && inputRangeEndRef) {
        const start = value?.[0] ? formatShortDate(value?.[0], dateFormat) : '';
        const end = value?.[1] ? formatShortDate(value[1], dateFormat) : '';
        inputRangeStartRef.value = start.replaceAll('/', '');
        inputRangeEndRef.value = end.replaceAll('/', '');
      }

      if (dateRange.start && dateRange.end) {
        setIsOpen(false);
      }

      return;
    }

    onChange?.(value as Date);
    if (inputRef) {
      inputRef.value = formatShortDate(value as Date, dateFormat);
    }

    setIsOpen(false);
  };

  const getInputValue = () => {
    return inputRef?.value;
  };

  const getInputRangeStartValue = () => {
    return inputRangeStartRef?.value;
  };

  const getInputRangeEndValue = () => {
    return inputRangeEndRef?.value;
  };

  const onClickOutside = (event: React.MouseEvent<HTMLDivElement>) => {
    const isClickInDatePicker = boxRef.current?.contains(event.target as Node);

    if (!isClickInDatePicker) {
      setIsOpen(false);
      getButtonProps().onClick();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      buttonRef.current?.focus();
      return;
    }

    if (event.key === 'Tab') {
      const focusedElement = event.target;
      const nextMonthButton = boxRef?.current?.querySelector<HTMLButtonElement>('.next-month');
      const todayButton = boxRef?.current?.querySelector<HTMLButtonElement>('.today-button');
      const dayFocused = boxRef?.current?.querySelector<HTMLDivElement>('.react-datepicker__day--keyboard-selected');

      if (focusedElement === dayFocused && event.shiftKey) {
        if (todayButton) {
          todayButton.focus();
          event.preventDefault();
          return;
        }
        nextMonthButton?.focus();
        event.preventDefault();
        return;
      }

      if (todayButton && !event.shiftKey) {
        if (todayButton === focusedElement) {
          dayFocused?.focus();
          event.preventDefault();
        }
        return;
      }

      if (focusedElement === nextMonthButton && !event.shiftKey) {
        dayFocused?.focus();
        event.preventDefault();
      }
    }
  };

  return {
    isOpen,
    isInputFocused,
    setIsInputFocused,
    setInputRef,
    setInputRangeStartRef,
    setInputRangeEndRef,
    boxRef,
    buttonRef,
    footerRef,
    buttonProps,
    disclosureProps,
    toogleFocusTodayDate,
    handleClickCalendarButton,
    onInputChange,
    onInputRangeStartChange,
    onInputRangeEndChange,
    onCalendarChange,
    getInputValue,
    getInputRangeStartValue,
    getInputRangeEndValue,
    onClickOutside,
    handleKeyDown
  };
};

export default useDatePicker;
